import React, { useEffect, useRef, useState } from 'react';
import { adminFetch } from '../../utils/adminApi';

function getImageList(item, pluralKey, singleKey) {
  const plural = Array.isArray(item?.[pluralKey]) ? item[pluralKey].filter(Boolean) : [];
  if (plural.length > 0) return plural;
  return item?.[singleKey] ? [item[singleKey]] : [];
}

const newTask = (index = 0) => ({
  id: `lab-task-${Date.now()}-${index}`,
  label: `Task ${index + 1}`,
  text: '',
  commandText: '',
  taskImages: [],
  solutionImages: [],
  solutionImage: ''
});

const blankLab = () => ({
  id: '',
  labNumber: '',
  topologyImage: '',
  sourceParts: [],
  tasks: [newTask(0)]
});

function getLabEditorRoute() {
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/admin/labs/new') return { mode: 'new' };
  const match = path.match(/^\/admin\/labs\/([^/]+)\/edit$/);
  return match ? { mode: 'edit', id: decodeURIComponent(match[1]) } : { mode: 'list' };
}

function pushLabRoute(route) {
  const nextPath = route === 'new'
    ? '/admin/labs/new'
    : route
      ? `/admin/labs/${encodeURIComponent(route)}/edit`
      : '/admin/labs';
  if (window.location.pathname !== nextPath) {
    window.history.pushState({ adminLabRoute: nextPath }, '', nextPath);
  }
}

function AdminLabs() {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [feedback, setFeedback] = useState(null);
  const topologyInputRef = useRef(null);

  const editorFromLab = (lab) => {
    const topologyPart = (lab.sourceParts || []).find((part) => /topology/i.test(`${part?.id || ''} ${part?.alt || ''}`)) || lab.sourceParts?.[0];
    return {
      id: lab.id || '',
      labNumber: lab.labNumber ?? '',
      topologyImage: lab.topologyImage || lab.sourceImage || topologyPart?.src || '',
      sourceParts: Array.isArray(lab.sourceParts) ? lab.sourceParts : [],
      sourceWidth: lab.sourceWidth || '',
      sourceHeight: lab.sourceHeight || '',
      sourceDisplayWidth: lab.sourceDisplayWidth || '',
      sourceDisplayHeight: lab.sourceDisplayHeight || '',
      topologyCropHeight: lab.topologyCropHeight || '',
      tasks: Array.isArray(lab.tasks) && lab.tasks.length > 0
        ? lab.tasks.map((task, index) => ({
          ...newTask(index),
          ...task,
          taskImages: getImageList(task, 'taskImages', 'taskImage'),
          solutionImages: getImageList(task, 'solutionImages', 'solutionImage')
        }))
        : [newTask(0)]
    };
  };

  const loadLabs = async () => {
    try {
      setLoading(true);
      const response = await adminFetch('/api/admin/labs');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load labs.');
      setLabs(Array.isArray(data.labs) ? data.labs : []);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Unable to load labs.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLabs(); }, []);

  useEffect(() => {
    const syncEditorRoute = () => {
      const route = getLabEditorRoute();
      if (route.mode === 'list') {
        setEditor(null);
      } else if (route.mode === 'new') {
        setEditor((current) => current || blankLab());
      } else {
        const lab = labs.find((item) => item.id === route.id);
        if (lab) setEditor((current) => current?.id === lab.id ? current : editorFromLab(lab));
      }
    };
    syncEditorRoute();
    window.addEventListener('popstate', syncEditorRoute);
    return () => window.removeEventListener('popstate', syncEditorRoute);
  }, [labs]);

  const updateEditor = (patch) => setEditor((previous) => ({ ...previous, ...patch }));

  const uploadImage = async (file, type, onComplete, progressKey = type) => {
    if (!file) return;
    setUploading(progressKey);
    setFeedback(null);
    try {
      const form = new FormData();
      form.append('image', file);
      form.append('type', type);
      const response = await adminFetch('/api/admin/upload-image', { method: 'POST', body: form });
      const data = await response.json();
      if (!response.ok || !data.url) throw new Error(data.error || 'Image upload failed.');
      onComplete(data.url);
      setFeedback({ type: 'success', message: 'Image uploaded. Save the lab to publish this change.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Image upload failed.' });
    } finally {
      setUploading('');
    }
  };

  const uploadImages = async (files, type, onComplete, progressKey = type) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (selectedFiles.length === 0) return;
    setUploading(progressKey);
    setFeedback(null);
    try {
      const urls = [];
      for (const file of selectedFiles) {
        const form = new FormData();
        form.append('image', file);
        form.append('type', type);
        const response = await adminFetch('/api/admin/upload-image', { method: 'POST', body: form });
        const data = await response.json();
        if (!response.ok || !data.url) throw new Error(data.error || 'Image upload failed.');
        urls.push(data.url);
      }
      onComplete(urls);
      setFeedback({ type: 'success', message: `${urls.length} image${urls.length === 1 ? '' : 's'} uploaded. Save the lab to publish this change.` });
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Image upload failed.' });
    } finally {
      setUploading('');
    }
  };

  const appendTaskImages = (taskIndex, pluralKey, singleKey, urls) => {
    setEditor((previous) => {
      const tasks = [...previous.tasks];
      const currentImages = getImageList(tasks[taskIndex], pluralKey, singleKey);
      const nextImages = [...currentImages, ...urls];
      tasks[taskIndex] = { ...tasks[taskIndex], [pluralKey]: nextImages, [singleKey]: nextImages[0] || '' };
      return { ...previous, tasks };
    });
  };

  const removeTaskImage = (taskIndex, pluralKey, singleKey, imageIndex) => {
    setEditor((previous) => {
      const tasks = [...previous.tasks];
      const nextImages = getImageList(tasks[taskIndex], pluralKey, singleKey).filter((_, index) => index !== imageIndex);
      tasks[taskIndex] = { ...tasks[taskIndex], [pluralKey]: nextImages, [singleKey]: nextImages[0] || '' };
      return { ...previous, tasks };
    });
  };

  const handleTopologyUpload = (event) => {
    const file = event.target.files?.[0];
    uploadImage(file, 'lab_topology', (url) => updateEditor({ topologyImage: url }));
    event.target.value = '';
  };

  const handleTaskUpload = (taskIndex, event) => {
    uploadImages(event.target.files, 'lab_solution', (urls) => {
      appendTaskImages(taskIndex, 'solutionImages', 'solutionImage', urls);
    }, `lab_solution_${taskIndex}`);
    event.target.value = '';
  };

  const saveTaskImage = (taskIndex, file) => {
    uploadImages([file], 'lab_solution', (urls) => {
      appendTaskImages(taskIndex, 'solutionImages', 'solutionImage', urls);
    }, `lab_solution_${taskIndex}`);
  };

  const saveTaskReferenceImage = (taskIndex, file) => {
    uploadImages([file], 'lab_task', (urls) => {
      appendTaskImages(taskIndex, 'taskImages', 'taskImage', urls);
    }, `lab_task_${taskIndex}`);
  };

  const handleTaskReferenceUpload = (taskIndex, event) => {
    uploadImages(event.target.files, 'lab_task', (urls) => {
      appendTaskImages(taskIndex, 'taskImages', 'taskImage', urls);
    }, `lab_task_${taskIndex}`);
    event.target.value = '';
  };

  const handleClipboardPaste = (event, target, taskIndex = null) => {
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find((item) => item.kind === 'file' && item.type.startsWith('image/'));
    if (!imageItem) return;
    event.preventDefault();
    const file = imageItem.getAsFile();
    if (!file) return;
    if (target === 'topology') {
      uploadImage(file, 'lab_topology', (url) => updateEditor({ topologyImage: url }));
    } else if (target === 'task' && taskIndex !== null) {
      saveTaskReferenceImage(taskIndex, file);
    } else if (taskIndex !== null) {
      saveTaskImage(taskIndex, file);
    }
  };

  const updateTask = (taskIndex, patch) => {
    setEditor((previous) => {
      const tasks = [...previous.tasks];
      tasks[taskIndex] = { ...tasks[taskIndex], ...patch };
      return { ...previous, tasks };
    });
  };

  const addTask = () => {
    setEditor((previous) => ({ ...previous, tasks: [...previous.tasks, newTask(previous.tasks.length)] }));
  };

  const removeTask = (taskIndex) => {
    setEditor((previous) => ({
      ...previous,
      tasks: previous.tasks.filter((_, index) => index !== taskIndex).map((task, index) => ({
        ...task,
        label: task.label || `Task ${index + 1}`
      }))
    }));
  };

  const openCreate = () => {
    setFeedback(null);
    pushLabRoute('new');
    const enteredNumbers = labs
      .map((lab) => Number.parseInt(lab.labNumber, 10))
      .filter((number) => Number.isFinite(number) && number > 0);
    const nextLabNumber = (enteredNumbers.length > 0 ? Math.max(...enteredNumbers) + 1 : 1).toString();
    setEditor({ ...blankLab(), labNumber: nextLabNumber });
  };

  const openEdit = (lab) => {
    setFeedback(null);
    pushLabRoute(lab.id);
    setEditor(editorFromLab(lab));
  };

  const saveLab = async (event) => {
    event.preventDefault();
    const labNumber = String(editor?.labNumber ?? '').trim();
    if (!/^\d+$/.test(labNumber) || Number(labNumber) < 1 || !editor?.topologyImage) {
      setFeedback({ type: 'error', message: 'Enter a valid Lab No and upload a topology image.' });
      return;
    }
    setSaving(true);
    try {
      const response = await adminFetch('/api/admin/labs', {
        method: 'POST',
        body: JSON.stringify({ ...editor, labNumber })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to save lab.');
      setFeedback({ type: 'success', message: 'Lab saved successfully.' });
      pushLabRoute(null);
      setEditor(null);
      await loadLabs();
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Unable to save lab.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteLab = async (lab) => {
    if (!window.confirm(`Delete Lab No ${lab.labNumber || lab.id}? This cannot be undone.`)) return;
    try {
      const response = await adminFetch(`/api/admin/labs/${encodeURIComponent(lab.id)}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to delete lab.');
      setFeedback({ type: 'success', message: 'Lab deleted.' });
      loadLabs();
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Unable to delete lab.' });
    }
  };

  return (
    <div className="admin-labs-view">
      {feedback && (
        <div className={`admin-lab-feedback ${feedback.type}`} role="status">
          <span>{feedback.message}</span>
          <button type="button" onClick={() => setFeedback(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className={`admin-card admin-lab-list-shell ${editor ? 'is-editing' : ''}`}>
        <div className="admin-card-header">
          <div>
            <h3 className="admin-card-title"><span>🧪</span> Guided Labs</h3>
            <p className="admin-lab-description">
              Create Labs with an original topology and revealable task solutions. Written instructions are optional.
            </p>
          </div>
          <button type="button" className="btn-admin-primary" onClick={openCreate}>+ Add Lab</button>
        </div>

        {loading ? (
          <div className="admin-empty-state">Loading labs…</div>
        ) : labs.length === 0 ? (
          <div className="admin-empty-state">
            <strong>No labs yet.</strong>
            <span>Create a guided lab here. Every lab in this list can be edited or removed.</span>
          </div>
        ) : (
          <div className="admin-lab-list">
            {labs.map((lab) => (
              <article key={lab.id} className="admin-lab-row">
                <div className="admin-lab-thumb">
                  {lab.topologyImage ? <img src={lab.topologyImage} alt="" /> : lab.sourceParts?.[0]?.src ? <img src={lab.sourceParts[0].src} alt="" /> : <span>LAB</span>}
                </div>
                <div className="admin-lab-row-copy">
                  <div className="admin-lab-row-title">Lab No {lab.labNumber || '—'}</div>
                  <div className="admin-lab-row-meta">{(lab.tasks || []).length} task{(lab.tasks || []).length === 1 ? '' : 's'}</div>
                </div>
                <div className="admin-lab-row-actions">
                  <button type="button" className="btn-table-action" onClick={() => openEdit(lab)}>Edit</button>
                  <button type="button" className="btn-table-action danger" onClick={() => deleteLab(lab)}>Delete</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {editor && (
          <form className="admin-card admin-lab-editor-page" onSubmit={saveLab}>
            <div className="admin-card-header admin-lab-editor-header">
              <div>
                <h3 className="admin-card-title"><span>🧪</span> {editor.id ? 'Edit Guided Lab' : 'Add Guided Lab'}</h3>
                <p className="admin-lab-modal-subtitle">Upload original artwork, then build the task-by-task reveal in one workspace.</p>
              </div>
              <button type="button" className="btn-admin-secondary" onClick={() => { pushLabRoute(null); setEditor(null); }}>← Back to Labs</button>
            </div>

            <div className="admin-lab-editor-body">
              <div className="admin-lab-form-grid">
                <label className="admin-field-label">
                  Lab No
                  <input
                    className="admin-input admin-lab-number-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    value={editor.labNumber}
                    onChange={(event) => updateEditor({ labNumber: event.target.value.replace(/\D/g, '') })}
                    placeholder="e.g. 6"
                  />
                </label>
              </div>

              <section className="admin-lab-upload-panel">
                <div>
                  <div className="admin-section-heading">Topology image</div>
                  <p>Use the original topology screenshot. It will be shown before the learner reveals tasks.</p>
                </div>
                <div
                  className="admin-lab-upload-row admin-lab-paste-zone"
                  tabIndex="0"
                  onPaste={(event) => handleClipboardPaste(event, 'topology')}
                  title="Click here and press Ctrl+V to paste an image"
                >
                  {editor.topologyImage && <img className="admin-lab-upload-preview" src={editor.topologyImage} alt="Topology preview" />}
                  <span className="admin-lab-paste-hint">Click here, then press <kbd>Ctrl</kbd> + <kbd>V</kbd> to paste an image</span>
                  <button type="button" className="btn-admin-secondary" onClick={() => topologyInputRef.current?.click()} disabled={uploading === 'lab_topology'}>
                    {uploading === 'lab_topology' ? 'Uploading…' : editor.topologyImage ? 'Replace topology' : 'Upload topology'}
                  </button>
                  {editor.topologyImage && (
                    <button type="button" className="admin-text-danger" onClick={() => updateEditor({ topologyImage: '' })}>
                      Remove image
                    </button>
                  )}
                  <input ref={topologyInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleTopologyUpload} hidden />
                </div>
              </section>

              <div className="admin-lab-tasks-heading">
                <div>
                  <div className="admin-section-heading">Tasks and solutions</div>
                  <p>Each task can include learner-facing instructions, typed CLI commands, and an original command screenshot.</p>
                </div>
              </div>

              <div className="admin-lab-task-editor-list">
                {editor.tasks.map((task, taskIndex) => (
                  <section key={task.id || taskIndex} className="admin-lab-task-editor">
                    <div className="admin-lab-task-editor-header">
                      <span className="admin-lab-task-index">{taskIndex + 1}</span>
                      <input className="admin-input admin-task-label-input" value={task.label} onChange={(event) => updateTask(taskIndex, { label: event.target.value })} aria-label={`Task ${taskIndex + 1} label`} />
                      {editor.tasks.length > 1 && <button type="button" className="admin-text-danger" onClick={() => removeTask(taskIndex)}>Remove</button>}
                    </div>
                    <label className="admin-field-label">
                      Task instructions (optional)
                      <textarea className="admin-textarea" rows="4" value={task.text} onChange={(event) => updateTask(taskIndex, { text: event.target.value })} placeholder="Write exactly what the learner must configure…" />
                    </label>
                    <div
                      className="admin-task-screenshot-row admin-lab-paste-zone admin-task-reference-upload"
                      tabIndex="0"
                      onPaste={(event) => handleClipboardPaste(event, 'task', taskIndex)}
                      title="Click here and press Ctrl+V to paste a task screenshot"
                    >
                      <div>
                        <div className="admin-field-label">Task screenshot (optional)</div>
                        <div className="admin-task-screenshot-preview-list">
                          {getImageList(task, 'taskImages', 'taskImage').map((image, imageIndex) => (
                            <div key={`${image}-${imageIndex}`} className="admin-task-screenshot-preview-item">
                              <img className="admin-task-screenshot-preview" src={image} alt={`Reference ${imageIndex + 1} for ${task.label}`} />
                              <button type="button" className="admin-text-danger admin-remove-image-button" onClick={() => removeTaskImage(taskIndex, 'taskImages', 'taskImage', imageIndex)}>
                                Remove image {imageIndex + 1}
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="admin-lab-paste-hint">Click here, then press <kbd>Ctrl</kbd> + <kbd>V</kbd> to paste a task screenshot</div>
                      </div>
                      <label className="btn-admin-secondary admin-upload-button">
                        {uploading === `lab_task_${taskIndex}` ? 'Uploading…' : getImageList(task, 'taskImages', 'taskImage').length > 0 ? 'Add more task screenshots' : 'Attach task screenshots'}
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={(event) => handleTaskReferenceUpload(taskIndex, event)} hidden />
                      </label>
                    </div>
                    <div className="admin-lab-task-details-grid">
                      <label className="admin-field-label">
                        Task written content (optional)
                        <textarea className="admin-textarea" rows="5" value={task.taskText || ''} onChange={(event) => updateTask(taskIndex, { taskText: event.target.value })} placeholder="Add extra task details, addressing tables, or reference notes…" />
                      </label>
                      <label className="admin-field-label">
                        Written commands (optional)
                        <textarea className="admin-textarea admin-command-textarea" rows="5" value={task.commandText || ''} onChange={(event) => updateTask(taskIndex, { commandText: event.target.value })} placeholder="R1# configure terminal&#10;R1(config)# …" />
                      </label>
                    </div>
                    <div
                      className="admin-task-screenshot-row admin-lab-paste-zone"
                      tabIndex="0"
                      onPaste={(event) => handleClipboardPaste(event, 'solution', taskIndex)}
                      title="Click here and press Ctrl+V to paste a command screenshot"
                    >
                      <div>
                        <div className="admin-field-label">Command screenshot (optional)</div>
                        <div className="admin-task-screenshot-preview-list">
                          {getImageList(task, 'solutionImages', 'solutionImage').map((image, imageIndex) => (
                            <div key={`${image}-${imageIndex}`} className="admin-task-screenshot-preview-item">
                              <img className="admin-task-screenshot-preview" src={image} alt={`Solution ${imageIndex + 1} for ${task.label}`} />
                              <button type="button" className="admin-text-danger admin-remove-image-button" onClick={() => removeTaskImage(taskIndex, 'solutionImages', 'solutionImage', imageIndex)}>
                                Remove image {imageIndex + 1}
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="admin-lab-paste-hint">Click here, then press <kbd>Ctrl</kbd> + <kbd>V</kbd> to paste a screenshot</div>
                      </div>
                      <label className="btn-admin-secondary admin-upload-button">
                        {uploading === `lab_solution_${taskIndex}` ? 'Uploading…' : getImageList(task, 'solutionImages', 'solutionImage').length > 0 ? 'Add more screenshots' : 'Attach screenshots'}
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={(event) => handleTaskUpload(taskIndex, event)} hidden />
                      </label>
                    </div>
                  </section>
                ))}
              </div>
              <button type="button" className="btn-admin-primary admin-add-task-button admin-add-task-bottom" onClick={addTask}>
                + Add another task
              </button>
            </div>

            <div className="admin-modal-footer admin-lab-editor-footer">
              <button type="button" className="btn-admin-secondary" onClick={() => { pushLabRoute(null); setEditor(null); }}>Cancel</button>
              <button type="submit" className="btn-admin-primary" disabled={saving || Boolean(uploading)}>{saving ? 'Saving…' : 'Save Lab'}</button>
            </div>
          </form>
      )}
    </div>
  );
}

export default AdminLabs;
